import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { fileUrl } from "../lib/api";

export const ImageLightbox = ({ src, alt = "Fotografía ampliada", title, onClose, testId = "image-lightbox" }) => {
  const [state, setState] = useState("loading");
  const closeRef = useRef(null);
  const openerRef = useRef(null);

  useEffect(() => {
    openerRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey, true);
      if (openerRef.current && openerRef.current.focus) openerRef.current.focus();
    };
  }, [onClose]);

  const trapFocus = (e) => {
    if (e.key !== "Tab") return;
    e.preventDefault();
    closeRef.current?.focus();
  };

  if (!src) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title || alt}
      data-testid={testId}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-3 backdrop-blur-sm"
      style={{ pointerEvents: "auto" }}
      onClick={onClose}
      onKeyDown={trapFocus}
    >
      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        aria-label="Cerrar visor de imagen"
        data-testid={`${testId}-close`}
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-hz-cyan"
      >
        <X className="h-6 w-6" />
      </button>
      {state === "loading" && (
        <Loader2 className="h-8 w-8 animate-spin text-white" data-testid={`${testId}-loading`} />
      )}
      {state === "error" && (
        <p className="rounded-lg bg-white/10 px-4 py-2 text-sm font-semibold text-white" data-testid={`${testId}-error`}>
          No se pudo cargar la imagen.
        </p>
      )}
      <img
        src={src}
        alt={alt}
        onLoad={() => setState("ready")}
        onError={() => setState("error")}
        onClick={(e) => e.stopPropagation()}
        data-testid={`${testId}-img`}
        className={`max-h-[85vh] max-w-[94vw] rounded-lg object-contain shadow-2xl sm:max-h-[90vh] sm:max-w-[90vw] ${state === "ready" ? "" : "hidden"}`}
      />
    </div>,
    document.body
  );
};

export const PhotoThumb = ({ src, alt = "Fotografía", className = "", title, testId = "photo-thumb" }) => {
  const [open, setOpen] = useState(false);
  if (!src) return null;
  const url = fileUrl(src);
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        aria-label={`Ampliar ${alt}`}
        data-testid={`${testId}-open`}
        className="cursor-zoom-in rounded-xl focus:outline-none focus:ring-2 focus:ring-hz-cyan"
      >
        <img src={url} alt={alt} className={className} data-testid={testId} />
      </button>
      {open && <ImageLightbox src={url} alt={alt} title={title || alt} onClose={() => setOpen(false)} testId={`${testId}-lightbox`} />}
    </>
  );
};
